import { cn } from "../lib/cn";

type IconButtonVariant = "outline" | "ghost";
type IconButtonSize = "sm" | "md";

const variantBase: Record<IconButtonVariant, string> = {
  outline:
    "bg-card border border-rule text-ink disabled:bg-recess disabled:text-faint",
  ghost: "bg-transparent text-ink-body disabled:text-faint",
};

const variantHover: Record<IconButtonVariant, string> = {
  outline: "hover:shadow-lift-lg hover:-translate-y-px",
  ghost: "hover:bg-smudge",
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: "size-7 text-sm",
  md: "size-9 text-base",
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon-only control has no visible label. */
  "aria-label": string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * Square icon-only button. Pass a single icon as the child; the label lives in
 * `aria-label`.
 */
export function IconButton({
  variant = "ghost",
  size = "md",
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center rounded-paper",
        "transition duration-150 ease-paper",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed",
        sizeClasses[size],
        variantBase[variant],
        !disabled && variantHover[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
